import { useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment, formatDateTime } from '../../../utils/dateUtils';

import {
  Box,
  Button,
  IconButton,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Divider,
  Input,
  Paper,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import HistoryIcon from '@mui/icons-material/History';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

// Utils
import messages from '../../../static/messages';

// Services
import propertiesServices from '../../../services/propertiesServices';
import integrationServices from '../../../services/integrationServices';

// Styles
import { button } from '../../../static/theme-styles';

// Mobile Info Row Component
const MobileInfoRow = ({ icon: Icon, label, value, highlight = false }) => {
  if (!value) return null;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'flex-start', 
      py: 1.5,
      borderBottom: '1px solid #f0f0f0',
      bgcolor: highlight ? '#fff3e0' : 'transparent',
      mx: highlight ? -2 : 0,
      px: highlight ? 2 : 0,
    }}>
      {Icon && <Icon sx={{ color: highlight ? '#ff9800' : '#757575', fontSize: 20, mr: 1.5, mt: 0.3 }} />}
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const PropertyDetailsTable = ({ property, id, refresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Build full address
  const fullAddress = [
    property?.streetNumber && property?.streetName ? `${property.streetNumber} ${property.streetName}` : null,
    property?.unit ? `Unit ${property.unit}` : null,
    property?.city,
    property?.state,
    property?.zipCode
  ].filter(Boolean).join(', ') || property?.address || 'Not specified';

  // Old address from source
  const oldAddress = property?.source 
    ? `${property.source.address}, ${property.source.city}, ${property.source.zipcode}`
    : null;

  // Mobile View
  if (isMobile) {
    return (
      <Fragment>
        <Box>
          {/* Full Address */}
          <MobileInfoRow 
            icon={LocationOnIcon} 
            label="Full Address" 
            value={fullAddress} 
          />

          {/* Old Address (if exists) */}
          {oldAddress && (
            <MobileInfoRow 
              icon={HistoryIcon} 
              label="Old Address Version" 
              value={oldAddress}
              highlight
            />
          )}

          {/* Created */}
          <MobileInfoRow 
            icon={CalendarTodayIcon} 
            label="Created" 
            value={moment(property?.createdAt).tz('America/Los_Angeles').format('MMM Do YYYY, h:mm a')} 
          />

          {/* Created By */}
          {property?.createdBy && (
            <MobileInfoRow 
              icon={PersonOutlineIcon} 
              label="Created By" 
              value={property.createdBy.fullName || `${property.createdBy.firstName || ''} ${property.createdBy.lastName || ''}`.trim()} 
            />
          )}

          {/* Last Update */}
          <MobileInfoRow 
            icon={UpdateIcon} 
            label="Last Update" 
            value={formatDateTime(property?.updatedAt, 'MMM Do YYYY, h:mm a')} 
          />
        </Box>

        <PropertyNotes {...{ notesList: property?.notes, id, refresh, address: fullAddress }} />
      </Fragment>
    );
  }

  // Desktop View - Original Table
  return (
    <Fragment>
      <TableContainer component={Paper}>
        <Table>
          <TableBody sx={{ backgroundColor: '#fff' }}>
            <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
              <TableCell sx={{ fontWeight: 600 }}>Full Address</TableCell>
              <TableCell>{fullAddress}</TableCell>
            </TableRow>
            {property?.source && (
              <TableRow sx={{ backgroundColor: '#ffe0e0' }}>
                <TableCell sx={{ fontWeight: 600 }}>Old Address Version</TableCell>
                <TableCell>
                  {property?.source?.address}, {property?.source?.city}, {property?.source?.zipcode}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell>{formatDateTime(property?.createdAt, 'MMM Do YYYY, h:mm:ss a')}</TableCell>
            </TableRow>
            {property?.createdBy && (
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                <TableCell>{property.createdBy.fullName || `${property.createdBy.firstName || ''} ${property.createdBy.lastName || ''}`.trim()}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Last Update</TableCell>
              <TableCell>{formatDateTime(property?.updatedAt, 'MMM Do YYYY, h:mm:ss a')}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <PropertyNotes {...{ notesList: property?.notes, id, refresh, address: fullAddress }} />
    </Fragment>
  );
};

export default PropertyDetailsTable;

const PropertyNotes = ({ notesList, id, refresh, address }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const confirm = useConfirm();
  const userId = JSON.parse(localStorage.getItem('user')).id;
  const userRole = JSON.parse(localStorage.getItem('user')).role;
  const [comment, setComment] = useState();
  const [editableNote, setEditableNote] = useState();
  const [loadingLegacy, setLoadingLegacy] = useState(false);

  // Action functions
  const onEditNote = (el) => setEditableNote(el);

  const onCancelEditNote = () => {
    confirm({
      title: messages.confirm_message.close_drawer,
      description: messages.confirm_message.close_drawer_description,
    })
      .then(() => setEditableNote(null))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Handler functions
  const handleChangeNote = (e) => {
    setEditableNote((prevState) => ({
      ...prevState,
      note: e.target.value,
    }));
  };

  const handleRemove = (noteId) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('Note'),
    })
      .then(() => removeNote(noteId))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Async functions
  const addNewNote = async () => {
    const notes = [
      ...notesList,
      {
        note: comment,
      },
    ];

    try {
      const response = await propertiesServices.update(id, { notes });

      if (response.status === 200) {
        setComment(null);
        toast.success(messages.success.update.note.save);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateNote = async () => {
    const notes = [
      ...notesList.filter((el) => el.id !== editableNote.id),
      {
        id: editableNote.id,
        note: editableNote.note,
      },
    ];

    try {
      const response = await propertiesServices.update(id, { notes });

      if (response.status === 200) {
        setEditableNote(null);
        toast.success(messages.success.update.note.save);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const removeNote = async (noteId) => {
    const notes = [...notesList.filter((el) => el.id !== noteId)];

    try {
      const response = await propertiesServices.update(id, { notes });

      if (response.status === 200) {
        toast.success(messages.success.update.note.remove);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Get notes from Legacy CRM
  const getLegacyNotes = async () => {
    if (!address) {
      toast.warning('No address available to search');
      return;
    }

    setLoadingLegacy(true);
    try {
      // Extract street address for search (remove city, state, zip)
      const streetAddress = address.split(',')[0].trim();
      
      const response = await integrationServices.getLegacyPropertyNotes(streetAddress);
      
      if (!response.data.found) {
        toast.info('No property found in Legacy CRM for this address');
        return;
      }

      if (!response.data.notes) {
        toast.info('Property found but no notes in Legacy CRM');
        return;
      }

      // Add legacy notes as a new note with prefix
      const legacyNote = `[LEGACY CRM NOTES]\n${response.data.notes}`;
      
      const notes = [
        ...notesList,
        { note: legacyNote },
      ];

      const updateResponse = await propertiesServices.update(id, { notes });

      if (updateResponse.status === 200) {
        toast.success('Legacy notes imported successfully!');
        refresh();
      }
    } catch (error) {
      console.error('Error getting legacy notes:', error);
      toast.error('Failed to get legacy notes');
    } finally {
      setLoadingLegacy(false);
    }
  };

  return (
    <Box sx={{ pt: 3, pb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>📝 Notes:</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={loadingLegacy ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
          onClick={getLegacyNotes}
          disabled={loadingLegacy}
          sx={{ textTransform: 'none' }}
        >
          {loadingLegacy ? 'Loading...' : 'Get Legacy Notes'}
        </Button>
      </Box>
      
      {notesList.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No notes yet
        </Typography>
      ) : (
        notesList.map((el) => (
          <Card key={el.id} variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              {editableNote?.id === el.id ? (
                <Box>
                  <TextField
                    defaultValue={editableNote.note}
                    name="editableNote"
                    id="editableNote"
                    onChange={handleChangeNote}
                    autoFocus
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button 
                      size="small" 
                      variant="contained" 
                      onClick={updateNote}
                      startIcon={<SaveIcon />}
                    >
                      Save
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={onCancelEditNote}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                    {el.note}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      {el.createdBy?.fullName || 'Unknown'} • {moment(el.createdAt).tz('America/Los_Angeles').format('L')}
                    </Typography>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => onEditNote(el)}
                        disabled={userId !== el.createdBy?.id && !['superadmin', 'admin', 'dispatcher'].includes(userRole)}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemove(el.id)}
                        disabled={userId !== el.createdBy.id && !['superadmin', 'admin', 'dispatcher'].includes(userRole)}
                      >
                        <DeleteForeverIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}

      <Box sx={{ mt: 2 }}>
        <TextField
          id="new-note"
          label="New Note"
          multiline
          rows={isMobile ? 3 : 4}
          value={comment || ''}
          fullWidth
          size={isMobile ? 'small' : 'medium'}
          onChange={(e) => setComment(e.target.value)}
        />

        <Button
          variant="contained"
          size={isMobile ? 'medium' : 'small'}
          fullWidth={isMobile}
          sx={{ ...button('secondary'), mt: 2 }}
          onClick={addNewNote}
          disabled={!comment}
        >
          Add Note
        </Button>
      </Box>
    </Box>
  );
};
